import {
  Portal,
  Form,
  FormHandlers,
  useModal,
  Message,
  Action,
  Spacing,
  ICON_CALENDAR_OUTLINE,
  HStack,
} from '@doveit/bricks';
import React from 'react';
import { mutate } from 'swr';
import {
  addWeeks, setHours, startOfHour, addMonths, formatDistance, isFuture,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { formatInputDate, formatInputTime, formDateTimeToDate } from '../../../utils/form';
import { saveColdLeadContactCallBack, saveLeadContactCallBackDate } from '../../../providers/api/call-center/callCenterLeadProvider';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { CONTACT_RID } from '../../../contact/hooks/use-contact/useContact';
import { CallContextType } from '../../../providers/api/dtos';
import { callBackFormSchema } from './CallbackAction.schema';
import { saveFeedbackContactCallBack } from '../../../providers/api/call-center/callCenterFeedbackProvider';

const ERROR_MESSAGE = 'Non è stato possibile aggiornare la data di prossimo contatto.';

export interface CallBackActionProps extends React.AriaAttributes {
  type: CallContextType,
  contactId: string,
  disabled?: boolean,
  onSuccess?: (callBackDate: string) => void,
  onError?: (error: Error) => void,
}

const CallBackAction: React.FC<CallBackActionProps> = ({
  type,
  contactId,
  disabled = false,
  onSuccess,
  onError,
  ...rest
}) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>() as React.MutableRefObject<FormHandlers>;

  const onSubmitClick = React.useCallback(() => {
    formRef.current.handleSubmit();
  }, []);

  const onSubmit = async ({ date, time }: { date: string, time: string }) => {
    setIsSaving(true);
    setHasError(false);

    const callbackDate = formDateTimeToDate(date, time);

    try {
      switch (type) {
        case CallContextType.LEAD:
          await saveLeadContactCallBackDate(contactId, callbackDate);
          break;
        case CallContextType.COLD_LEAD:
          await saveColdLeadContactCallBack(contactId, callbackDate);
          break;
        case CallContextType.SURVEY:
          await saveFeedbackContactCallBack(contactId, callbackDate);
          break;
        default:
      }

      setIsSaving(false);
      modal.close();
      onSuccess?.(`${date} ${time}`);
      mutate([CONTACT_RID, contactId]);
    } catch (err: any) {
      setIsSaving(false);
      setHasError(true);
      onError?.(err);
    }
  };

  return (
    <>
      <Action
        {...rest}
        label="Da richiamare"
        emphasis="high"
        iconLeft={{ path: ICON_CALENDAR_OUTLINE }}
        disabled={disabled}
        onClick={() => modal.open()}
      />
      <Portal>
        <SimpleModal
          title="A che ora vuoi richiamare il contatto?"
          {...modal}
          footer={(
            <Action
              label="Salva"
              loading={isSaving}
              color="primary"
              emphasis="high"
              onClick={onSubmitClick}
            />
          )}
        >
          <Form
            onSubmit={onSubmit}
            innerRef={formRef}
            initialValues={{
              date: formatInputDate(new Date()),
              time: '',
            }}
            loading={isSaving}
            validationSchema={callBackFormSchema}
            preventEnterKeypress
          >
            {({ values, setFieldValue }) => {
              const setDatetime = (datetime: Date) => {
                setFieldValue('date', formatInputDate(datetime));
                setFieldValue('time', formatInputTime(datetime));
              };

              return (
                <>
                  <HStack>
                    <Action
                      onClick={() => setDatetime(startOfHour(setHours(new Date(), 18)))}
                      label="Stasera"
                      size="S"
                      emphasis="high"
                      data-ref="this-evening-button"
                    />
                    <Action
                      onClick={() => setDatetime(addWeeks(new Date(), 2))}
                      label="Tra due settimane"
                      size="S"
                      emphasis="high"
                      data-ref="two-weeks-button"
                    />
                    <Action
                      onClick={() => setDatetime(addMonths(new Date(), 1))}
                      label="Tra un mese"
                      size="S"
                      emphasis="high"
                      data-ref="one-month-button"
                    />
                  </HStack>
                  <Form.Group label="Seleziona data e ora per richiamare il contatto">
                    <Form.Item size={1 / 2}>
                      <Form.Input
                        name="date"
                        type="date"
                        aria-label="Campo per inserire la data"
                        required
                      />
                    </Form.Item>
                    <Form.Item size={1 / 2}>
                      <Form.Input
                        type="time"
                        aria-label="Campo per inserire l'ora"
                        name="time"
                        required
                      />
                    </Form.Item>
                    {isFuture(new Date(`${values?.date} ${values?.time}`)) && (
                      <Form.Item>
                        <div data-ref="recall-label">
                          Prossima chiamata tra: <strong>{formatDistance(new Date(), new Date(`${values?.date} ${values?.time}`), { locale: it })}</strong>
                        </div>
                      </Form.Item>
                    )}
                  </Form.Group>

                  {hasError && (
                    <Spacing margin={[200, 0, 0]}>
                      <Message
                        type="critical"
                        message={ERROR_MESSAGE}
                      />
                    </Spacing>
                  )}
                </>
              );
            }}
          </Form>
        </SimpleModal>
      </Portal>
    </>
  );
};

export default CallBackAction;
